import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const STORAGE_KEY = "fixanor.theme";
const THEME_OPTIONS = ["light", "dark"];

const isSupportedTheme = (theme) => THEME_OPTIONS.includes(theme);

const getInitialTheme = () => {
  if (typeof window === "undefined") {
    return "light";
  }

  try {
    const savedTheme = window.localStorage.getItem(STORAGE_KEY);
    if (isSupportedTheme(savedTheme)) {
      return savedTheme;
    }
  } catch {}

  return window.matchMedia?.("(prefers-color-scheme: dark)").matches ? "dark" : "light";
};

const ThemeContext = createContext(null);

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(getInitialTheme);

  useEffect(() => {
    try {
      window.localStorage.setItem(STORAGE_KEY, theme);
    } catch {}

    document.documentElement.dataset.theme = theme;
    document.documentElement.style.colorScheme = theme;
  }, [theme]);

  const value = useMemo(() => {
    const toggleTheme = () => {
      setTheme((currentTheme) => (currentTheme === "dark" ? "light" : "dark"));
    };

    return {
      theme,
      isDark: theme === "dark",
      setTheme: (nextTheme) => {
        if (isSupportedTheme(nextTheme)) {
          setTheme(nextTheme);
        }
      },
      toggleTheme,
    };
  }, [theme]);

  return <ThemeContext.Provider value={value}>{children}</ThemeContext.Provider>;
}

export function useTheme() {
  const context = useContext(ThemeContext);

  if (!context) {
    throw new Error("useTheme must be used inside ThemeProvider");
  }

  return context;
}
